import { useState } from "react";
import axios from "axios";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal, Eyebrow, Check } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DANI_PORTRAIT_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_brand-embodied/artifacts/8f799c0f074c936b_DSCF4852%206.webp";

const inputCls =
  "w-full border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-bone placeholder:text-white/30 transition-colors focus:border-forest";

export const Apply = () => {
  const reduce = useReducedMotion();
  const [form, setForm] = useState({
    name: "",
    email: "",
    instagram: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError("Name, email and what you want to build are required.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/applications`, form);
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Something went wrong. Please try again."
      );
    }
  };

  return (
    <section
      id="apply"
      className="bg-ink py-24 lg:py-32"
      data-testid="apply-section"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>Application</Eyebrow>
              <h2 className="mt-6 font-mont text-3xl font-bold leading-[1.15] tracking-tight text-bone sm:text-4xl lg:text-5xl">
                Curious?{" "}
                <em className="italic text-forest">Build</em> with us.
              </h2>
              <p className="mt-6 max-w-md font-mont text-sm leading-relaxed text-white/60">
                Tell Dani what you want to build. Applications are read
                personally — this isn&rsquo;t a funnel, and it&rsquo;s not a
                sales pitch either.
              </p>
              <ul className="mt-10 space-y-2" data-testid="apply-bullets">
                {[
                  "8 weeks, live, in a small group",
                  "It starts with a conversation, not a pitch",
                  "Founding rate of $1,500 for the first 6 seats",
                ].map((t, i) => (
                  <li
                    key={t}
                    className={`flex items-center gap-3 px-4 py-3 text-sm text-white/70 ${
                      i % 2 === 0 ? "bg-white/[0.12]" : "bg-black"
                    }`}
                  >
                    <Check />
                    {t}
                  </li>
                ))}
              </ul>

              <div className="mt-10 max-w-sm" data-testid="apply-portrait">
                <img
                  src={DANI_PORTRAIT_IMG}
                  alt="Dani Gutierrez, mentor of The Embodied Creator"
                  className="img-bw aspect-[4/5] w-full object-cover"
                />
                <p className="mt-4 font-serif text-sm italic text-stone">
                  Dani Gutierrez — your mentor
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: reduce ? 0 : 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9 }}
              className="border border-white/10 bg-white/[0.04] p-7 backdrop-blur-sm sm:p-10"
              data-testid="apply-form-card"
            >
              {status === "success" ? (
                <div
                  className="py-10 text-center"
                  data-testid="apply-success-message"
                >
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-forest">
                    <svg width="22" height="22" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2 7.5L5.5 11L12 3" stroke="#1F4D3A" strokeWidth="1.8" strokeLinecap="square" />
                    </svg>
                  </span>
                  <h3 className="mt-6 font-serif text-3xl font-light text-bone">
                    Application received.
                  </h3>
                  <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-white/60">
                    Dani reads every application personally. Expect a reply by
                    email about next steps.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="apply-form" noValidate>
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="name" className="mb-2 block text-[11px] uppercase tracking-[0.22em] text-white/50">
                        Name
                      </label>
                      <input
                        id="name"
                        data-testid="apply-name-input"
                        className={inputCls}
                        value={form.name}
                        onChange={set("name")}
                        placeholder="Your name"
                        autoComplete="name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-2 block text-[11px] uppercase tracking-[0.22em] text-white/50">
                        Email
                      </label>
                      <input
                        id="email"
                        type="email"
                        data-testid="apply-email-input"
                        className={inputCls}
                        value={form.email}
                        onChange={set("email")}
                        placeholder="you@email.com"
                        autoComplete="email"
                      />
                    </div>
                    <div>
                      <label htmlFor="instagram" className="mb-2 block text-[11px] uppercase tracking-[0.22em] text-white/50">
                        Instagram
                      </label>
                      <input
                        id="instagram"
                        data-testid="apply-instagram-input"
                        className={inputCls}
                        value={form.instagram}
                        onChange={set("instagram")}
                        placeholder="@yourhandle"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="mb-2 block text-[11px] uppercase tracking-[0.22em] text-white/50">
                        What do you want to build?
                      </label>
                      <textarea
                        id="message"
                        rows={4}
                        data-testid="apply-message-input"
                        className={`${inputCls} resize-none`}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="The brand, the offer, the version of you on the other side of these 8 weeks…"
                      />
                    </div>
                  </div>

                  {error && (
                    <p className="mt-4 text-sm text-[#d47f6e]" data-testid="apply-error-message">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="apply-submit-button"
                    className="mt-8 w-full bg-forest px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-[#2a6b50] disabled:opacity-60"
                  >
                    {status === "sending" ? "Sending…" : "Submit application"}
                  </button>
                  <p className="mt-4 text-center text-xs text-white/40">
                    We reply by email. Your details are used for this
                    application only.
                  </p>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Apply;
